import { useTranslation } from "@/hooks/useTranslation";
import { cn } from "@/lib/utils";
import { PromptDTO } from "@shaarp/common";
import { Pencil, Pin, Star, Trash2 } from "lucide-react";
import { memo, MouseEvent } from "react";

type PromptActionButtonsProps = {
  prompt: PromptDTO;
  canEdit?: boolean;
  isFavorite?: boolean;
  isPinned?: boolean;
  onEdit?: (prompt: PromptDTO) => void;
  onRemove?: (prompt: PromptDTO) => void;
  onToggleFavorite?: (prompt: PromptDTO) => void;
  onTogglePinned?: (prompt: PromptDTO) => void;
  className?: string;
  showOnHoverOnly?: boolean;
};

const ACTION_BUTTON_BASE = cn(
  "flex items-center justify-center h-7 w-7 rounded-md transition-colors",
);

function PromptActionButtons({
  prompt,
  canEdit = false,
  isFavorite = false,
  isPinned = false,
  onEdit,
  onRemove,
  onToggleFavorite,
  onTogglePinned,
  className,
  showOnHoverOnly = true,
}: PromptActionButtonsProps) {
  const { t } = useTranslation();

  const handleEdit = (e: MouseEvent) => {
    e.stopPropagation();
    onEdit?.(prompt);
  };

  const handleRemove = (e: MouseEvent) => {
    e.stopPropagation();
    onRemove?.(prompt);
  };

  const handleToggleFavorite = (e: MouseEvent) => {
    e.stopPropagation();
    onToggleFavorite?.(prompt);
  };

  const handleTogglePinned = (e: MouseEvent) => {
    e.stopPropagation();
    onTogglePinned?.(prompt);
  };

  const hasActions =
    (canEdit && (onEdit || onRemove)) || onToggleFavorite || onTogglePinned;

  if (!hasActions) {
    return null;
  }

  return (
    <div className={cn("flex items-center shrink-0 gap-1", className)}>
      {canEdit && onEdit && (
        <button
          type="button"
          onClick={handleEdit}
          className={cn(
            ACTION_BUTTON_BASE,
            "hover:bg-accent transition-opacity",
            showOnHoverOnly && "opacity-0 group-hover:opacity-100 touch:opacity-100",
          )}
          aria-label={t("prompt.card.actions.edit")}
        >
          <Pencil className="w-3.5 h-3.5" />
        </button>
      )}
      {canEdit && onRemove && (
        <button
          type="button"
          onClick={handleRemove}
          className={cn(
            ACTION_BUTTON_BASE,
            "text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-opacity",
            showOnHoverOnly && "opacity-0 group-hover:opacity-100 touch:opacity-100",
          )}
          aria-label={t("prompt.card.actions.delete")}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
      {onTogglePinned && (
        <button
          type="button"
          onClick={handleTogglePinned}
          className={cn(
            ACTION_BUTTON_BASE,
            "transition-opacity",
            isPinned
              ? "text-blue-500 hover:text-blue-600"
              : "text-muted-foreground hover:text-foreground hover:bg-accent",
            showOnHoverOnly &&
              (isPinned ? "opacity-100" : "opacity-0 group-hover:opacity-100 touch:opacity-100"),
          )}
          aria-label={
            isPinned
              ? t("prompt.card.actions.unpin")
              : t("prompt.card.actions.pin")
          }
        >
          <Pin className={cn("w-3.5 h-3.5", isPinned && "fill-current")} />
        </button>
      )}
      {onToggleFavorite && (
        <button
          type="button"
          onClick={handleToggleFavorite}
          className={cn(
            ACTION_BUTTON_BASE,
            "transition-opacity",
            isFavorite
              ? "text-yellow-500 hover:text-yellow-600"
              : "text-muted-foreground hover:text-foreground hover:bg-accent",
            showOnHoverOnly &&
              (isFavorite
                ? "opacity-100"
                : "opacity-0 group-hover:opacity-100 touch:opacity-100"),
          )}
          aria-label={
            isFavorite
              ? t("prompt.card.actions.removeFromFavorites")
              : t("prompt.card.actions.addToFavorites")
          }
        >
          <Star className={cn("w-3.5 h-3.5", isFavorite && "fill-current")} />
        </button>
      )}
    </div>
  );
}

export default memo(PromptActionButtons);
